import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const sectionRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      [row1Ref, row2Ref].forEach((rowRef) => {
        gsap.from(rowRef.current.querySelectorAll('.about-reveal'), {
          y: 40,
          opacity: 0,
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.12,
          scrollTrigger: { trigger: rowRef.current, start: 'top 80%' },
        });

        gsap.from(rowRef.current.querySelector('.about-art'), {
          opacity: 0,
          scale: 0.9,
          y: 40,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: { trigger: rowRef.current, start: 'top 80%' },
        });
      });

      gsap.to(blob1Ref.current, {
        rotate: 8,
        ease: 'none',
        scrollTrigger: {
          trigger: row1Ref.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });

      gsap.to(blob2Ref.current, {
        rotate: -8,
        ease: 'none',
        scrollTrigger: {
          trigger: row2Ref.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });

      gsap.utils.toArray('.about-float').forEach((el, i) => {
        gsap.to(el, {
          y: i % 2 === 0 ? -18 : 18,
          duration: 3 + i * 0.4,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#faf9fb] py-20 sm:py-24 lg:py-28 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-6xl mx-auto">
        {/* Row 1 — Text Left, Art Right */}
        <div
          ref={row1Ref}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
        >
          <div className="text-left">
            <h2 className="about-reveal text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.15] tracking-tight text-gray-900 mb-6">
              Why our salon exists
            </h2>
            <p className="about-reveal text-base text-gray-600 leading-relaxed mb-5">
              Everyone wants to look their best, but walking into a salon can feel like a gamble —
              will they understand the vision, will the cut actually suit you, will you leave
              feeling like yourself?
            </p>
            <p className="about-reveal text-base text-gray-600 leading-relaxed">
              We built this salon to take the guesswork out of looking good. Every consultation,
              every cut, every color is designed around who you are and how you want to be seen —
              so you leave confident, every single time.
            </p>
          </div>

          <div className="about-art relative h-[340px] sm:h-[380px] lg:h-[420px] flex items-center justify-center">
            <div
              ref={blob1Ref}
              className="absolute inset-0 m-auto w-[92%] h-[88%] bg-[#F05A35]/10"
              style={{ borderRadius: '62% 38% 54% 46% / 48% 56% 44% 52%' }}
            />
            <div className="about-float relative z-10 w-[58%] aspect-square rounded-full overflow-hidden shadow-2xl ring-4 ring-white">
              <img
                src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&q=80&w=900"
                alt="Stylist at work"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="about-float absolute top-6 right-8 z-20 w-20 h-20 rounded-2xl overflow-hidden shadow-xl ring-4 ring-white">
              <img
                src="https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&q=80&w=400"
                alt="Hair color"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="about-float absolute bottom-6 left-6 z-20 w-24 h-24 rounded-2xl overflow-hidden shadow-xl ring-4 ring-white">
              <img
                src="https://images.unsplash.com/photo-1580618672591-eb180b1a973f?auto=format&fit=crop&q=80&w=400"
                alt="Finished look"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="my-20 lg:my-24 border-t border-gray-200" />

        {/* Row 2 — Art Left, Text Right */}
        <div
          ref={row2Ref}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
        >
          <div className="about-art relative h-[340px] sm:h-[380px] lg:h-[420px] flex items-center justify-center order-2 lg:order-1">
            <div
              ref={blob2Ref}
              className="absolute inset-0 m-auto w-[92%] h-[88%] bg-[#6EC1E4]/15"
              style={{ borderRadius: '46% 54% 38% 62% / 56% 44% 56% 44%' }}
            />
            <div className="about-float relative z-10 w-[58%] aspect-square rounded-full overflow-hidden shadow-2xl ring-4 ring-white">
              <img
                src="https://images.unsplash.com/photo-1600948836101-f9ffda59d250?auto=format&fit=crop&q=80&w=900"
                alt="Salon interior"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="about-float absolute top-4 left-4 z-20 w-20 h-20 rounded-2xl overflow-hidden shadow-xl ring-4 ring-white">
              <img
                src="https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&q=80&w=400"
                alt="Styling"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="about-float absolute bottom-8 right-4 z-20 w-24 h-24 rounded-2xl overflow-hidden shadow-xl ring-4 ring-white">
              <img
                src="https://images.unsplash.com/photo-1595959183082-7b570b7e08e2?auto=format&fit=crop&q=80&w=400"
                alt="Product"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="text-left order-1 lg:order-2">
            <h2 className="about-reveal text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.15] tracking-tight text-gray-900 mb-6">
              Why you need us
            </h2>
            <p className="about-reveal text-base text-gray-600 leading-relaxed mb-8">
              You can book any salon in town, and most will give you a decent cut. But a great look
              isn't just about technique — it's about understanding your face, your hair, your
              routine, and what makes you feel like you. That's the gap most salons don't close,
              and that's exactly what we do.
            </p>
            <Link
              to="/bookings"
              className="about-reveal inline-flex items-center justify-center bg-[#F05A35] hover:bg-[#d94a28] text-white font-bold tracking-wider uppercase text-sm px-8 py-4 rounded-full transition-colors"
            >
              Book Appointment
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
